import React, { useState, useEffect } from 'react';
import { leadsAPI } from '../../../services/api';
import { Button, Card } from '../../../components/ui';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };

export default function AudioTab({ leadId }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ call_notes: '', call_date: '', duration_seconds: '' });
  const [file, setFile] = useState(null);

  const load = async () => {
    try {
      const res = await leadsAPI.getAudio(leadId);
      setRecordings(res.data?.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [leadId]);

  const upload = async () => {
    if (form.call_date) {
      const year = new Date(form.call_date).getFullYear();
      if (year < 2000 || year > 2099) { toast.error('Please enter a valid year (2000–2099)'); return; }
    }
    setSaving(true);
    try {
      const fd = new FormData();
      if (file) fd.append('audio', file);
      fd.append('call_notes', form.call_notes);
      if (form.call_date) fd.append('call_date', form.call_date);
      if (form.duration_seconds) fd.append('duration_seconds', form.duration_seconds);
      await leadsAPI.uploadAudio(leadId, fd);
      toast.success('Call log saved');
      setFile(null);
      setForm({ call_notes: '', call_date: '', duration_seconds: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally { setSaving(false); }
  };

  const fmtDuration = (s) => {
    if (!s) return '';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Audio Calls & Call Notes</h3>

      {/* Upload Form */}
      <Card style={{ marginBottom: 20 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 12 }}>Log a Call</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Call Date</label>
            <input type="datetime-local" value={form.call_date} onChange={e => setForm(f => ({ ...f, call_date: e.target.value }))} min="2000-01-01T00:00" max="2099-12-31T23:59" style={inp} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Duration (seconds)</label>
            <input type="number" value={form.duration_seconds} onChange={e => setForm(f => ({ ...f, duration_seconds: e.target.value }))} style={inp} placeholder="e.g. 180" />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Call Notes</label>
          <textarea value={form.call_notes} onChange={e => setForm(f => ({ ...f, call_notes: e.target.value }))} style={{ ...inp, minHeight: 80, resize: 'vertical' }} placeholder="What was discussed, key points, next steps…" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Audio File (optional)</label>
          <input type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg" onChange={e => setFile(e.target.files[0])} style={{ fontSize: 13 }} />
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Max 50 MB. MP3, WAV, M4A. Stored temporarily — migrate to S3 for production.</p>
        </div>
        <Button variant="primary" size="sm" onClick={upload} disabled={saving || !form.call_notes.trim()}>
          {saving ? 'Saving…' : '📞 Save Call Log'}
        </Button>
      </Card>

      {/* History */}
      {loading ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</p> : recordings.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 24 }}>No call logs yet</p>
      ) : recordings.map((r, i) => (
        <Card key={r._id || i} style={{ marginBottom: 10, padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13 }}>📞</span>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {r.call_date ? new Date(r.call_date).toLocaleString('en-IN') : new Date(r.createdAt).toLocaleString('en-IN')}
                  {r.duration_seconds && ` · ${fmtDuration(r.duration_seconds)}`}
                </span>
                {r.file_name && <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '1px 6px', borderRadius: 6, fontWeight: 600 }}>🎵 Audio</span>}
              </div>
              <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}>{r.call_notes}</p>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>by {r.logged_by?.name || '—'}</p>
            </div>
            {r.file_url && (
              <a href={r.file_url} target="_blank" rel="noopener noreferrer"
                style={{ padding: '5px 10px', background: '#e0e7ff', color: '#4338ca', borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', marginLeft: 10 }}>
                ↓ Audio
              </a>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
